/** Seven clouds that drift around the planet from size 3: one instanced mesh of four merged puffs. */
import * as THREE from 'three';

import { hash2 } from '@core/math';

import { softMaterial } from './scene';

const COUNT = 7;

export interface Clouds {
  /** Move the clouds for this frame; `visible` from level 3. */
  update(dt: number, radius: number, visible: boolean, reduced: boolean): void;
}

const _m = new THREE.Matrix4();
const _p = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _s = new THREE.Vector3(1, 1, 1);
const _look = new THREE.Matrix4();
const origin = new THREE.Vector3();
const up = new THREE.Vector3(0, 1, 0);

export function createClouds(parent: THREE.Object3D): Clouds {
  const puffs: THREE.BufferGeometry[] = [];
  for (let j = 0; j < 4; j++) {
    puffs.push(
      new THREE.SphereGeometry(0.3 + hash2(1, j) * 0.2, 16, 12).translate(
        j * 0.34 - 0.5,
        hash2(j, 1) * 0.16,
        hash2(4, j) * 0.2,
      ),
    );
  }
  const positions: number[] = [];
  const normals: number[] = [];
  const index: number[] = [];
  let offset = 0;
  for (const g of puffs) {
    const p = g.attributes['position'];
    const n = g.attributes['normal'];
    if (!p || !n || !g.index) continue;
    for (let i = 0; i < p.array.length; i++) {
      positions.push(p.array[i] ?? 0);
      normals.push(n.array[i] ?? 0);
    }
    for (let i = 0; i < g.index.count; i++) index.push((g.index.array[i] ?? 0) + offset);
    offset += p.count;
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geometry.setIndex(index);
  const mesh = new THREE.InstancedMesh(
    geometry,
    softMaterial(0xffffff, { roughness: 1, transparent: true, opacity: 0.95 }),
    COUNT,
  );
  mesh.castShadow = true;
  mesh.frustumCulled = false;
  mesh.count = 0;
  parent.add(mesh);
  const orbits = Array.from({ length: COUNT }, (_, k) => ({
    a: hash2(k, 9) * 6.28,
    b: (hash2(k, 8) - 0.5) * 1.6,
    s: 0.05 + hash2(k, 7) * 0.05,
  }));

  return {
    update(dt, radius, visible, reduced) {
      if (!visible) {
        mesh.count = 0;
        return;
      }
      const rr = radius + 2.1;
      orbits.forEach((o, k) => {
        if (!reduced) o.a += dt * o.s;
        _p.set(Math.cos(o.a) * rr, Math.sin(o.b) * rr * 0.6 + rr * 0.2, Math.sin(o.a) * rr);
        _look.lookAt(_p, origin, up);
        _q.setFromRotationMatrix(_look);
        _m.compose(_p, _q, _s);
        mesh.setMatrixAt(k, _m);
      });
      mesh.count = COUNT;
      mesh.instanceMatrix.needsUpdate = true;
    },
  };
}
